<template>
    <b-modal
        :visible="visible"
        @hide="$emit('close')"
        title="Nuevo Seguimiento"
        size="md"
        centered
        no-close-on-backdrop
    >
        <div class="tracing-form">
            <b-row>
                <!-- Estado -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label required">Estado</label>
                        <el-select
                            v-model="form.status"
                            placeholder="Seleccione estado"
                            class="w-100"
                            size="small"
                        >
                            <el-option
                                v-for="status in statusOptions"
                                :key="status.value"
                                :label="status.label"
                                :value="status.value"
                            />
                        </el-select>
                    </div>
                </b-col>
                <!-- Fecha del Reporte -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label required">Fecha del reporte</label>
                        <el-date-picker
                            v-model="form.report_date"
                            type="date"
                            placeholder="Fecha del reporte"
                            format="dd/MM/yyyy"
                            value-format="yyyy-MM-dd"
                            size="small"
                            class="w-100"
                        />
                    </div>
                </b-col>
            </b-row>

            <!-- Condición Reportada -->
            <div class="form-group">
                <label class="form-label required">Condición reportada</label>
                <el-input
                    v-model="form.reported_condition"
                    type="textarea"
                    :rows="4"
                    placeholder="Describa la condición reportada..."
                    maxlength="2000"
                    show-word-limit
                />
            </div>

            <!-- Adjunto -->
            <div class="form-group">
                <label class="form-label">Adjunto (máx. 20 MB)</label>
                <div class="file-upload-wrapper">
                    <input 
                        type="file" 
                        ref="fileInput"
                        @change="handleFileChange"
                        class="file-input-hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    />
                    <div class="file-upload-box" @click="$refs.fileInput.click()">
                        <span v-if="!form.attachment" class="text-muted">
                            <i class="fa fa-paperclip mr-1"></i> Seleccione archivo
                        </span>
                        <span v-else class="text-primary">
                            <i class="fa fa-file mr-1"></i> {{ form.attachment.name }}
                            <i class="fa fa-times ml-2 text-danger" @click.stop="removeFile"></i>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Advertencia de cambio de estado -->
            <div class="alert-info-box" v-if="plan && form.status !== plan.status">
                <i class="fa fa-info-circle mr-2"></i>
                El estado del plan de acción cambiará a <strong>{{ form.status }}</strong>
            </div>
        </div>

        <template #modal-footer>
            <b-button variant="secondary" @click="$emit('close')">
                Cancelar
            </b-button>
            <b-button 
                variant="primary" 
                @click="handleSubmit"
                :disabled="loading || !form.status"
            >
                <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-save mr-1"></i>
                Guardar Seguimiento
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'CreateTracingModal',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        plan: {
            type: Object,
            default: null,
        },
        companyId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            form: {
                status: 'Abierto',
                report_date: this.getTodayDate(),
                reported_condition: '',
                attachment: null,
            },
            statusOptions: [
                { value: 'Abierto', label: 'Abierto' },
                { value: 'Proceso', label: 'En Proceso' },
                { value: 'Cerrado por verificar', label: 'Cerrado por verificar' },
                { value: 'Cerrado', label: 'Cerrado' },
            ],
        };
    },
    watch: {
        visible(newVal) {
            if (newVal && this.plan) {
                this.form.status = this.plan.status || 'Abierto';
                this.form.report_date = this.getTodayDate();
                this.form.reported_condition = '';
                this.form.attachment = null;
            }
        },
    },
    methods: {
        ...mapActions('skudo/tracings', ['createTracing']),

        getTodayDate() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },

        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 20 * 1024 * 1024) {
                    this.$alertWarning('El archivo no debe superar 20 MB');
                    return;
                }
                this.form.attachment = file;
            }
        },

        removeFile() {
            this.form.attachment = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        async handleSubmit() {
            if (!this.form.status) {
                this.$alertWarning('Debe seleccionar un estado');
                return;
            }
            if (!this.form.report_date) {
                this.$alertWarning('Debe seleccionar la fecha del reporte');
                return;
            }
            if (!this.form.reported_condition) {
                this.$alertWarning('Debe ingresar la condición reportada');
                return;
            }

            this.loading = true;
            try {
                const response = await this.createTracing({
                    company_id: this.companyId,
                    action_plan_id: this.plan.id,
                    status: this.form.status,
                    report_date: this.form.report_date,
                    reported_condition: this.form.reported_condition,
                    attachment: this.form.attachment,
                });

                if (response.data) {
                    this.$alertSuccess(response.message || 'Seguimiento creado correctamente');
                    // El state ya se actualizó via mutation en el action
                    this.$emit('created');
                } else {
                    this.$alertError(response.message || 'Error al crear el seguimiento');
                }
            } catch (error) {
                this.$alertError(error.message || 'Error al crear el seguimiento');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.tracing-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
}

.form-label.required::after {
    content: ' *';
    color: #dc2626;
}

.plan-info-box {
    background: #f3f4f6;
    border-radius: 6px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
}

.info-label {
    color: #6b7280;
}

.info-value {
    color: #374151;
    font-weight: 500;
}

.alert-info-box {
    background: #dbeafe;
    border: 1px solid #93c5fd;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 0.85rem;
    color: #1e40af;
}

/* File Upload Styles */
.file-upload-wrapper {
    position: relative;
}

.file-input-hidden {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.file-upload-box {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.2s;
    font-size: 0.85rem;
}

.file-upload-box:hover {
    border-color: #409eff;
}

.file-upload-box .fa-times {
    cursor: pointer;
}

.file-upload-box .fa-times:hover {
    color: #dc2626 !important;
}
</style>
